import { Box, styled } from '@mui/material';
import { ActionButton } from '../button';

export const ChatRoot = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  position: 'relative',
}));

export const MessageList = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  padding: theme.spacing(1, 1.5),
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${theme.palette.borders.main}`,
  borderBottom: `1px solid ${theme.palette.borders.main}`,
  display: 'flex',
  flexDirection: 'column',
}));

export const MessageInput = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexShrink: 0,
  borderLeft: `1px solid ${theme.palette.borders.main}`,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(0.75, 1),
  gap: theme.spacing(1),
}));

export const ContentEditorWrapper = styled(Box)(({ theme }) => ({
  display: 'block',
  flexGrow: 1,
  flexShrink: 1,
  backgroundColor: theme.palette.background.field,
  borderRadius: theme.spacing(0.5),
  minWidth: 0,
  // Ringing the wrapper keeps the indicator on its rounded field rather than the bare textarea.
  '&:focus-within': theme.mixins.focusRing,
}));

export const ContentEditor = styled('textarea')(({ theme }) => ({
  display: 'block',
  width: '100%',
  border: 'none',
  outline: 'none', // ContentEditorWrapper draws the focus ring
  resize: 'none',
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  fontSize: 15,
  fontWeight: 500,
  lineHeight: 1.35,
  padding: theme.spacing(1, 1.5),
  boxSizing: 'border-box',
  overflow: 'hidden',
  '&::placeholder': {
    color: theme.palette.text.secondary,
  },
}));

export const ChatActions = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flexGrow: 0,
  flexShrink: 1,
}));

export const ChatActionsRow = styled(Box)(() => ({
  display: 'flex',
}));

export const ChatActionButton = styled(ActionButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  '& svg': {
    color: theme.palette.primary.dark,
    fontSize: 22,
  },

  '&:hover svg': {
    opacity: 0.6,
  },

  '&.Mui-disabled svg': {
    opacity: 0.4,
  },
}));

export const TypingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: theme.spacing(0.5, 0, 0, 0),
  marginTop: 'auto',
}));

export const MessageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  marginBottom: 8,
}));

export const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  maxWidth: '85%',
  padding: 8,
  fontSize: 14,
  lineHeight: 1.4,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  border: `1px solid ${theme.palette.borders.main}`,
  borderTopLeftRadius: isUser ? theme.spacing(1) : 0,
  borderTopRightRadius: theme.spacing(1),
  borderBottomLeftRadius: theme.spacing(1),
  borderBottomRightRadius: isUser ? 0 : theme.spacing(1),
  backgroundColor: isUser ? theme.palette.primary.dark : theme.palette.background.default,
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  // Markdown links are bare anchors; on the dark bubble the browser's link blue would vanish.
  ...(isUser && { '& a': { color: 'inherit' } }),

  '& p, & ul, & ol, & pre, & blockquote, & li, & h1, & h2, & h3, & h4, & h5, & h6': {
    margin: 0,
    '&:not(:last-child)': {
      marginBottom: '1em',
    },
  },

  '& ul, & ol': {
    paddingLeft: 18,
    listStylePosition: 'outside',
  },

  '& pre': {
    padding: 8,
    overflowX: 'auto',
    borderRadius: 6,
  },

  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
}));
